import { Component, inject, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core'
import { AsyncPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common'
import { LessonTileTermComponent } from "../../../lesson/tile/term/term.component"
import { LessonTileCompletionComponent } from "../../../lesson/tile/completion/completion.component"
import { BehaviorSubject, distinctUntilChanged, firstValueFrom, merge, takeUntil } from 'rxjs'
import { DialogComponent, DotFlashingComponent, TileComponent } from '@nx-superprep/shared'
import { LibraryService, ModuleTemplate, PerformanceStats, ShapeType, StudyService } from '@nx-superprep/backend/firebase'
import { Concept } from '@nx-superprep/backend/firebase'
import { countTitle, getRgbColorFromHex, resolveProperty, log, luminance } from '@nx-superprep/utils'
import { LessonTile, LessonTileService } from '@nx-superprep/study'
import { MatDialog } from '@angular/material/dialog'
import { TooltipModule } from 'ngx-bootstrap/tooltip'
import { BsModalService } from 'ngx-bootstrap/modal'

export interface LessonTerm {name:string, translation:string, progress:number, count: number}

@Component({
  selector: 'pop-ui-lesson-terms',
  standalone: true,
  templateUrl: './lesson-terms.component.html',
  styleUrls: ['./lesson-terms.component.scss'],
  imports: [
    NgClass, NgStyle, AsyncPipe, NgTemplateOutlet,
    TooltipModule,
    LessonTileTermComponent,
    LessonTileCompletionComponent,
    DotFlashingComponent,
  ],
  providers: [BsModalService]
})
export class LessonTermsComponent extends TileComponent implements OnInit, OnDestroy {

  private _lesson: LessonTile|undefined
  @Input()
  get lesson() { return this._lesson }
  set lesson(value) {
    this._lesson = value
    this.element = { ...this.element, content: value }
    this.finished = resolveProperty(this.lesson?.finished)
    this.subject = this.lesson?.subject ?? ''
    this.description = this.lesson?.description ?? ''
    this.subjectBackgroundColor = this._getSubjectBackgroundColor(this.subject)
    this.subjectTextColor = this._getSubjectTextColor(this.subjectBackgroundColor)
    this.updateData()
  }

  private _stats: PerformanceStats[] = []
  @Input()
  get stats() {
    return this._stats
  }
  set stats(value) {
    this._stats = value
  }

  @Input()
  override get scrollType() {
    return this.isWeb ? 'content' : 'none'
  }
  override set scrollType(value) {
    super.scrollType = value ?? 'none'
  }

  override shape: ShapeType = 'rect'

  finished = false
  subject = ''
  description = ''
  subjectBackgroundColor = ''
  subjectTextColor = '#000'

  terms = new BehaviorSubject<Concept[]>([])

  _defaultActions = ['add', 'try']
  actions = this._defaultActions.map((a) => this.createAction(a))

  actionClass(action: string) {
    return {
      add: `${this.subscribed ? 'added' : 'add'} ${this.submitted ? 'disabled' : ''}`,
      try: `${this.finished ? 'done' : 'try'} ${this.submitted ? 'disabled' : ''}`
    }[action] || 'grey'
  }

  actionText(action: string) {
    return {
      add: this.subscribed ? 'Added' : (this.adding ? 'Adding' : (this.removing ? 'Removing' : 'Add Unit')),
      try: this.finished ? 'Practice' : this.subscribed ? 'Play now' : 'Preview'
    }[action] || 'grey'
  }

  actionTip(action: string) {
    if (!this.isWeb || this.submitted) return ''
    return {
      add: `click to ${this.subscribed ? 'remove' : 'add'} lesson`,
      try: this.finished ? 'Practice' : `click to ${this.subscribed ? 'play now' : 'preview'}`
    }[action] || 'grey'
  }

  iconColor(action: string) {
    return {
      add: `/assets/icons/${this.subscribed ? '#33CA7F' : 'black'}`,
      try: `/assets/icons/${this.finished ? 'black' : 'black'}`
    }[action] || 'white'
  }

  iconURL(action: string) {
    return {
      add: `/assets/icons/${this.subscribed ? 'iconcheckmark-filled.svg' : 'iconadd.svg'}`,
      try: this.finished ? '' : '/assets/icons/iconplaybutton.svg'
    }[action] || ''
  }

  iconClass(action: string) {
    return {
      add: this.submitted ? 'loading' : '',
      try: ''
    }[action] || ''
  }

  iconALT(action: string) {
    return this.iconURL(action).split('/').pop() ?? ''
  }

  createAction(name: string) {
    return {
      name,
      class: this.actionClass(name),
      text: this.actionText(name),
      iconURL: this.iconURL(name),
      iconALT: this.iconALT(name),
      color: this.subjectBackgroundColor,
    }
  }

  override get countTitle() {
    return countTitle(this.terms.value, 'item', 'items')
  }

  get subscribed() {
    return resolveProperty(this.lesson?.subscribed)
  }

  get isInToday() {
    return this.lesson?.activeDate?.isToday()
  }

  private submitted = false
  private adding = false
  private removing = false

  isValid(lesson = this.lesson): boolean {
    return !!lesson && resolveProperty(lesson.isValid)
  }

  isActionable(action: string) {
    return this.isValid() //&& !(action === 'study' && this.finished)
  }

  onAction(action: string) {
    if (this.submitted) { return }
    this.submitted = true
    action = this.subscribed
      ? action === 'add'
        ? 'del'
        : action === 'try' && !this.finished ? 'study' : action
      : action === 'try' ? 'quiz' : action
    if (!this.isActionable(action)) {
      log.debug(`${action} disabled`)
      this.submitted = false
      return
    }
    if (!this.lesson?.id) {
      log.debug(`no id`, this.lesson)
      this.submitted = false
      return
    }
    if (!this.lesson.action) {
      log.debug(`no action for: ${this.lesson.id}`)
      this.submitted = false
      return
    }
    this.adding = action === 'add'
    this.removing = action === 'del'
    log.debug(action, this.lesson)
    const _action = this.lesson.action
    const isAssigned = resolveProperty(this.lesson.isAssigned)
    this.askForConsent(isAssigned && this.removing).then(consent => (
      (!consent ? Promise.resolve(false) : _action(action))
      .then(() => {
        this.actions = this._defaultActions.map((a) => this.createAction(a))
      })
    ))
    .finally(() => { this.submitted = false; this.adding = false; this.removing = false })
  }

  readonly dialog = inject(MatDialog);

  askForConsent(shouldAsk = false): Promise<boolean> {
    if (!shouldAsk) { return Promise.resolve(true)}
    const dialogRef = this.dialog.open(DialogComponent, {
      data: {title:'Remove Unit ?', message:'This is a teacher-assigned unit. Are you sure you wamt to remove it ?', accept: 'Remove', decline: 'Cancel'},
    });

    return firstValueFrom(dialogRef.afterClosed())
  }

  readonly modalService = inject(BsModalService)

  updateData() {
    this.onCancel.next()
    if (!this.lesson?.id) { return }
    this.actions = this._defaultActions.map((a) => this.createAction(a))
    this.libraryService.getModules(this.lesson.id)
      .pipe(takeUntil(merge(this.onDestroy, this.onCancel)))
      .subscribe((lessons) => {
        if (!lessons[0]) { return }
        const value = this._createLessonTile(lessons[0])
        this._lesson = value
        this.element = { ...this.element, content: value }
        this.finished = resolveProperty(this.lesson?.finished)
        this.subject = this.lesson?.subject ?? ''
        this.description = this.lesson?.description ?? ''
        this.subjectBackgroundColor = this._getSubjectBackgroundColor(this.subject)
        this.subjectTextColor = this._getSubjectTextColor(this.subjectBackgroundColor)
      })
    this.libraryService
      .getConceptStats(this.lesson.id)
      .pipe(takeUntil(merge(this.onDestroy, this.onCancel)))
      .subscribe((concepts) => {
        this.terms.next(concepts)
      })
  }

  get isLoaded() {
    return !this.isValid() || this.terms.value.length > 0
  }

  get isSubscribed() {
    return resolveProperty(this.libraryService.currentLesson?.subscribed)
  }

  private _getSubjectBackgroundColor(subject:string) {
    switch(subject.toLowerCase()) {
      default: return '#E3E3E3'
    }
  }

  private _getSubjectTextColor(backgroundColor: string) {
    return luminance(getRgbColorFromHex(backgroundColor)) > 0.5 ? '#000' : '#FFF'
  }

  private lessonTileService = inject(LessonTileService);

  private _createLessonTile(m: LessonTile | ModuleTemplate) {
    return this.lessonTileService.createLessonTile(m, {submitted: this.submitted})
  }

  private parent: Element | undefined

  override ngOnDestroy() {
    super.ngOnDestroy()
    if (this.parent) {
      this.renderer.removeClass(this.parent, 'd-none')
    }
  }

  override ngOnInit() {
    super.ngOnInit()
    merge(this.studyService.includedModules, this.studyService.getDayPlanAndModules())
      .pipe(takeUntil(this.onDestroy), distinctUntilChanged())
      .subscribe(() => {
        if (this.lesson) { this.lesson.subscribed = !!this.studyService.getPlanModule(this.lesson.id) }
        this.actions = this._defaultActions.map((a) => this.createAction(a))
      })
    this.parent = document.getElementsByClassName('library-sliding-right-container')[0]
    if (!this.isWeb) {
      if (this.parent) { this.renderer.addClass(this.parent, 'd-none') }
    } else {
      const elementStyle = document.getElementById('lessonTermsContainer')?.style
      if (elementStyle && this.parent) {
        const height = this.parent.getBoundingClientRect().height
        elementStyle.height = `${height}px`
      }
    }
    this.scrollType = this.isWeb ? 'content' : 'none'
  }

  override get rootClassName() { return `${super.rootClassName}${this.isContentScroll ? '' : ' scrollable'} ` }

  readonly renderer = inject(Renderer2)
  readonly studyService = inject(StudyService)
  readonly libraryService = inject(LibraryService)

  constructor() {
    super()
    super.rootClassName = 'lesson-terms-root-class-name'
  }
}
